import { zodResolver } from '@hookform/resolvers/zod';
import AddBusinessRounded from '@mui/icons-material/AddBusinessRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import ShoppingCartCheckoutRounded from '@mui/icons-material/ShoppingCartCheckoutRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import VisibilityOffRounded from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import {
  Alert,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { AuthLayout } from './AuthLayout';
import { DesktopDownloads } from './DesktopDownloads';
import { authButtonSx, authSecondarySx } from './styles';
import { useAuth } from './AuthProvider';

const schema = z.object({
  shopSlug: z.string().trim().min(1, 'Enter your shop ID'),
  login: z.string().trim().min(1, 'Enter your email or phone'),
  password: z.string().min(1, 'Enter your password'),
});
type Values = z.infer<typeof schema>;

function lastShop(): string {
  try {
    return localStorage.getItem('sm.lastShop') ?? '';
  } catch {
    return '';
  }
}

/** `pos` signs in straight to the full-screen point of sale (cashier devices bookmark /pos/login). */
export function LoginPage({ target = 'app' }: { target?: 'app' | 'pos' }) {
  const pos = target === 'pos';
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { shopSlug: lastShop(), login: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      await login(values);
      try {
        localStorage.setItem('sm.lastShop', values.shopSlug);
      } catch {
        /* ignore */
      }
      const from = (location.state as { from?: string } | null)?.from ?? (pos ? '/pos' : '/');
      void navigate(from, { replace: true });
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 429
          ? 'Too many attempts. Please wait a minute and try again.'
          : e instanceof ApiError && e.status < 500
            ? 'The shop ID, login or password is not correct.'
            : 'Could not reach the server. Check your connection and try again.',
      );
    }
  });

  const icon = (Icon: typeof StorefrontRounded) => (
    <InputAdornment position="start">
      <Icon sx={{ fontSize: 20, color: 'text.disabled' }} />
    </InputAdornment>
  );

  return (
    <AuthLayout
      title={pos ? 'Point of Sale' : 'Welcome back'}
      subtitle={
        pos
          ? 'Sign in to open the register.'
          : 'Sign in to your shop to pick up where you left off.'
      }
      footer={
        pos ? (
          <Button component={RouterLink} to="/login" variant="text" fullWidth>
            Sign in to the back office instead
          </Button>
        ) : (
          <>
            <Divider sx={{ mb: { xs: 1.75, sm: 2.5 }, color: 'text.secondary', fontSize: 12.5 }}>
              New to PackPilot?
            </Divider>
            <Button
              component={RouterLink}
              to="/register"
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<AddBusinessRounded />}
              sx={authSecondarySx}
            >
              Create a shop or restore a backup
            </Button>
            <Stack direction="row" sx={{ justifyContent: 'center', mt: { xs: 0.5, sm: 1.5 } }}>
              <Button
                component={RouterLink}
                to="/pos/login"
                variant="text"
                size="small"
                startIcon={<ShoppingCartCheckoutRounded />}
                sx={{ color: 'text.secondary', fontWeight: 600 }}
              >
                Cashier? Open the Point of Sale
              </Button>
            </Stack>
            {/* Not inside the desktop app itself (it exposes window.packpilot). */}
            {!('packpilot' in window) && <DesktopDownloads />}
          </>
        )
      }
    >
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <Stack spacing={{ xs: 2, sm: 2.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            size="medium"
            label="Shop ID"
            autoComplete="organization"
            placeholder="e.g. ahmed-hardware"
            {...register('shopSlug')}
            error={Boolean(errors.shopSlug)}
            helperText={errors.shopSlug?.message}
            slotProps={{ input: { startAdornment: icon(StorefrontRounded) } }}
          />
          <TextField
            size="medium"
            label="Email or phone"
            autoComplete="username"
            autoFocus={Boolean(lastShop())}
            {...register('login')}
            error={Boolean(errors.login)}
            helperText={errors.login?.message}
            slotProps={{ input: { startAdornment: icon(PersonOutlineRounded) } }}
          />
          <TextField
            size="medium"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            {...register('password')}
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            slotProps={{
              input: {
                startAdornment: icon(LockOutlined),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOffRounded fontSize="small" />
                      ) : (
                        <VisibilityRounded fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            loading={isSubmitting}
            fullWidth
            endIcon={<ArrowForwardRounded />}
            sx={authButtonSx}
          >
            {pos ? 'Sign in to the register' : 'Sign in'}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
